import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import last from 'lodash/last'

import { getPostById, postIepReviewAnalysis } from '../../api'
import { NotificationsContext } from '../../app/contexts'
import { useAppUser, useAppUserRefresh, useCurrentSite, useParentViewCapability } from '../../app/hooks'
import { CAPABILITY_FINISH_ACCOUNT } from '../../arch/capabilityConstants'
import { HTTP_CONFLICT, HTTP_FORBIDDEN } from '../../arch/constants'
import { user } from '../../auth'
import {
  clearIepReviewState,
  getIepReviewErrorHandler,
  getIepReviewOutcome,
  iepReviewViewerPath,
  readIepReviewState,
  writeIepReviewState
} from '../../domain/iepReview'
import { getIep } from '../../domain/plan'
import { modelIepPlan } from '../../iep/planModelers'
import model from '../../model'
import { PROGRESS_IEP } from '../../progress/constants'
import { useFileUpload } from '../../hooks/fileUpload'
import {
  trackIepAnalysisRefused,
  trackIepUploadCompleted,
  trackIepUploadRejected,
  trackIepUploadStarted
} from '../../tracking/segment'

import { ACCEPTED_FILE_TYPES, REFUSAL_REASON_ANALYSIS_USED } from './constants'
import {
  INLINE_UPLOAD_ERRORS,
  REJECTION_REASONS,
  getUploadErrorMessage,
  selectionErrorFor,
  uploadIepReviewFile
} from './uploadFiles'

const getPostIfAvailable = async postId => {
  try {
    return await getPostById(postId)
  } catch (error) {
    return null
  }
}

const useIepReviewState = () => {
  const [iepReviewState, setIepReviewState] = useState(readIepReviewState)

  const saveIepReviewState = useCallback(nextState => {
    if (nextState) {
      writeIepReviewState(nextState)
    } else {
      clearIepReviewState()
    }
    setIepReviewState(nextState)
  }, [])

  return [iepReviewState, saveIepReviewState]
}

const useIepProgressUpdates = () => {
  const { visibleProgressUpdates } = useContext(NotificationsContext)

  return useMemo(
    () => visibleProgressUpdates.filter(({ progressType }) => progressType === PROGRESS_IEP),
    [visibleProgressUpdates]
  )
}

// This hook derives, live, the IEPs that are already in progress, plus the given previous IEP (the failed one, or
// whichever one the review status last named). The claim hook reads the list at claim time, so that those IEPs’
// progress events aren’t mistaken for the new upload’s.
const usePreviousIepIds = previousIepId => {
  const iepProgressUpdates = useIepProgressUpdates()

  return useMemo(
    () => [...new Set([previousIepId, ...iepProgressUpdates.map(({ documentId }) => documentId)])].filter(Boolean),
    [previousIepId, iepProgressUpdates]
  )
}

// This hook claims staged uploads for an analysis and sorts out the server’s answer. A conflict means an analysis
// is already under way for this parent (for example, one their email confirmation just started), so the parent
// follows that one instead; its IEP may already be among the IDs to ignore (through its progress events, or a
// status the parent’s account carried in), so a fresh status says which ID not to ignore. A refusal means the
// parent has used up their free analysis. The returned claim resolves to the IDs for the analyzing step to
// ignore, or to null when the parent should stay put.
const useIepReviewClaim = previousIepId => {
  const refreshAppUser = useAppUserRefresh()
  const previousIepIds = usePreviousIepIds(previousIepId)

  const [claiming, setClaiming] = useState(false)
  const [analysisRefused, setAnalysisRefused] = useState(false)
  const [error, setError] = useState(null)

  const handleApiError = useMemo(() => getIepReviewErrorHandler(setError), [])

  const idsToIgnoreForRunningAnalysis = useCallback(async () => {
    try {
      await refreshAppUser()
    } catch (error) {
      // No-op—the status on hand is the best we have.
    }

    const runningIepId = user()?.iepReviewStatus?.documentId

    return previousIepIds.filter(id => id !== runningIepId)
  }, [refreshAppUser, previousIepIds])

  const claim = useCallback(
    async payload => {
      setClaiming(true)
      setError(null)

      try {
        if (await postIepReviewAnalysis(payload)) {
          return previousIepIds
        }
      } catch (error) {
        if (error.response?.status === HTTP_CONFLICT) {
          return idsToIgnoreForRunningAnalysis()
        }

        if (error.response?.status === HTTP_FORBIDDEN) {
          trackIepAnalysisRefused(REFUSAL_REASON_ANALYSIS_USED)
          setAnalysisRefused(true)
        } else {
          handleApiError(error)
        }
      }

      setClaiming(false)

      return null
    },
    [previousIepIds, idsToIgnoreForRunningAnalysis, handleApiError]
  )

  return { claim, claiming, analysisRefused, error, setError }
}

const useIepReviewPlan = (iepId, onError) => {
  const [iep, setIep] = useState()
  const [post, setPost] = useState()
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt(attempt => attempt + 1), [])

  useEffect(() => {
    let active = true

    setIep(undefined)
    setPost(undefined)

    const loadPlan = async () => {
      try {
        const [iepResponse, postResponse] = await Promise.all([getIep(iepId), getPostIfAvailable(iepId)])
        if (active) {
          setIep(modelIepPlan(iepResponse))
          setPost(postResponse && model.post(postResponse))
        }
      } catch (error) {
        if (active) {
          onError(error)
        }
      }
    }

    loadPlan()
    return () => {
      active = false
    }
  }, [iepId, attempt, onError])

  return { iep, setIep, post, reload }
}

// This hook holds the IEP review rules for a parent’s routing. `target` is where a parent with an unfinished
// account goes for its IEP review, with `targetState` to carry along; `zipCodeSkipped` lets such a parent view a
// route that allows it without a ZIP code; and `zipCodeState` brings a parent who leaves an IEP review route for
// the ZIP code page back to it afterwards.
const useIepReviewRedirect = () => {
  const appUser = useAppUser()
  const { pathname, search } = useLocation()
  const { unfinishedAccountAllowed } = useCurrentSite() ?? {}

  const { isParentCapable } = useParentViewCapability()

  const unfinishedAccount = isParentCapable(CAPABILITY_FINISH_ACCOUNT)

  const iepReview = {
    target: null,
    targetState: undefined,
    zipCodeSkipped: unfinishedAccount && Boolean(unfinishedAccountAllowed),
    zipCodeState: undefined
  }

  if (unfinishedAccountAllowed) {
    iepReview.zipCodeState = { redirectDestinationPath: `${pathname}${search}` }
  }

  if (!unfinishedAccount || unfinishedAccountAllowed) {
    return iepReview
  }

  // An account whose analysis is still running isn’t sent anywhere: the analysis email brings the parent back,
  // and the account is sent on to the review from wherever it is once the analysis has an outcome.
  const { childId } = appUser?.iepReviewStatus ?? {}
  const { readyIepId, retry } = getIepReviewOutcome(appUser?.iepReviewStatus) ?? {}

  if (childId && readyIepId) {
    iepReview.target = iepReviewViewerPath(childId, readyIepId)
    iepReview.targetState = { finishAccount: true }
  } else if (retry) {
    iepReview.target = `${iepReviewViewerPath(retry.clientId, retry.failedIepId)}/retry`
  }

  return iepReview
}

const fileTypesOf = files => [...new Set(files.map(file => file.type))]

const uploadFinished = entry => Boolean(entry.mediaId)

// This hook checks, uploads, and tracks the files a parent picks for an IEP review. Spread `inputProps` onto a hidden
// file input and call `chooseFiles` to open the picker. Rejections belong next to whatever the parent clicked;
// `uploadErrorMessage` is for an error snackbar.
const useIepFileUpload = onBatchUpload => {
  const inputRef = useRef(null)

  const [selectionError, setSelectionError] = useState(null)

  const handleBatchUploaded = useCallback(
    batch => {
      trackIepUploadCompleted(batch.length, fileTypesOf(batch.map(({ file }) => file)))

      onBatchUpload(
        batch.map(({ file, mediaId, uploadedFile }) => ({
          stagingKey: mediaId,
          fileName: file.name,
          file: uploadedFile
        }))
      )
    },
    [onBatchUpload]
  )

  const {
    uploadFiles,
    onDragOver,
    onDragLeave,
    setDragging,
    dragging,
    error: uploadError,
    setError: setUploadError,
    fileInProgress,
    uploadLog,
    uploading
  } = useFileUpload({
    onUpload: handleBatchUploaded,
    uploadFile: uploadIepReviewFile,
    getErrorMessage: getUploadErrorMessage
  })

  const currentBatch = last(uploadLog) ?? []
  const currentBatchSize = currentBatch.length
  const currentFileNumber = Math.min(currentBatch.filter(uploadFinished).length + 1, currentBatchSize)

  const inlineUploadError = INLINE_UPLOAD_ERRORS.includes(uploadError)
  const rejection = selectionError ?? (inlineUploadError ? uploadError : null)
  const uploadErrorMessage = inlineUploadError ? null : uploadError

  useEffect(() => {
    if (INLINE_UPLOAD_ERRORS.includes(uploadError)) {
      trackIepUploadRejected(REJECTION_REASONS[uploadError], currentBatchSize)
    }
  }, [uploadError, currentBatchSize])

  const selectFiles = files => {
    const error = selectionErrorFor(files)
    setSelectionError(error)
    setUploadError(null)

    if (error) {
      trackIepUploadRejected(REJECTION_REASONS[error], files.length)
      return
    }

    if (files.length > 0) {
      trackIepUploadStarted(files.length, fileTypesOf(files))
      uploadFiles(files)
    }
  }

  const handleFileChange = event => {
    selectFiles(Array.from(event.currentTarget.files))
    event.currentTarget.value = ''
  }

  const chooseFiles = event => inputRef.current.click()

  return {
    chooseFiles,
    selectFiles,
    inputProps: {
      ref: inputRef,
      type: 'file',
      hidden: true,
      multiple: true,
      accept: ACCEPTED_FILE_TYPES.join(','),
      onChange: handleFileChange
    },
    onDragOver,
    onDragLeave,
    setDragging,
    dragging,
    uploading,
    fileInProgress,
    currentFileNumber,
    currentBatchSize,
    rejection,
    uploadErrorMessage,
    setUploadError
  }
}

export {
  useIepReviewState,
  useIepProgressUpdates,
  usePreviousIepIds,
  useIepReviewClaim,
  useIepReviewPlan,
  useIepReviewRedirect,
  useIepFileUpload
}
